import '@discretize/typeface-menomonia';
import { Fab, useMediaQuery, useTheme, Zoom } from '@mui/material';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import 'typeface-fira-mono';
import 'typeface-muli';
import 'typeface-raleway';
import MenuIcon from '../assets/svg/MenuIcon';
import {
  startUpdates as discordStartUpdates,
  stopUpdates as discordStopUpdates,
} from '../state/discord';
import {
  startUpdates as fractalDailiesStartUpdates,
  stopUpdates as fractalDailiesStopUpdates,
} from '../state/fractals/dailies';
import Container, { ContainerProps as ExtContainerProps } from './Container';
import ErrorBoundary from './ErrorBoundary';
import Footer from './navigation/Footer';
import MoveUpFAB from './navigation/MoveUpFAB';
import Navigation from './navigation/Navigation';

const useStyles = makeStyles()((theme) => ({
  image: {
    position: 'fixed !important' as any,
    width: '100vw',
    height: '100vh',
    left: 0,
    bottom: 0,
    opacity: 0.7,
    zIndex: -1,
  },
  navigationFab: {
    position: 'fixed',
    zIndex: theme.zIndex.drawer,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[8],
    transition: `${theme.transitions.create(
      ['background-color', 'box-shadow', 'transform'],
      {
        duration: theme.transitions.duration.shorter,
      },
    )} !important`,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
      boxShadow: theme.shadows[16],
    },
  },
}));

export type Location = {
  pathname: string;
};

interface LayoutProps {
  children: React.ReactNode;
  location: Location;
  fabNavigation?: boolean;
  fabPosition?: {
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  };
  ContainerProps?: ExtContainerProps;
  disableBackground?: boolean;
  disableContainer?: boolean;
}

function Layout({
  location,
  children,
  fabNavigation,
  fabPosition = {
    top: 16,
    left: 16,
  },
  ContainerProps,
  disableBackground = false,
  disableContainer = false,
}: LayoutProps) {
  const { classes } = useStyles();

  const theme = useTheme();
  const widthDownMd = useMediaQuery(theme.breakpoints.down('lg'));
  const widthDownXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [state, setState] = React.useState({ open: false });
  const dispatch = useDispatch();

  useEffect(() => {
    const serverId = process.env.GATSBY_DISCORD_SERVER_ID;
    dispatch(
      discordStartUpdates({
        serverId,
        updateInterval: 1000 * 30,
      }),
    );
    dispatch(fractalDailiesStartUpdates());
    return () => {
      dispatch(discordStopUpdates({ serverId }));
      dispatch(fractalDailiesStopUpdates());
    };
  }, []);

  const { open } = state;

  const useFab = fabNavigation || widthDownMd;
  const posFab = (widthDownXs && { bottom: 16, right: 16 }) || fabPosition;
  const bg = !widthDownXs && !disableBackground;

  const {
    backgroundImage: { childImageSharp },
  } = useStaticQuery(graphql`
    {
      backgroundImage: file(
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "backgrounds/chaos_bg.png" }
      ) {
        childImageSharp {
          gatsbyImageData
        }
      }
    }
  `);

  return (
    <>
      {bg && (
        <GatsbyImage
          image={childImageSharp.gatsbyImageData}
          className={classes.image}
          alt="Background picture"
        />
      )}

      <ErrorBoundary location="navigation">
        {useFab && (
          <Zoom in={!open} timeout={200} unmountOnExit>
            <Fab
              className={classes.navigationFab}
              onClick={() => setState({ open: true })}
              style={posFab}
              aria-label="Menu"
            >
              <MenuIcon height={40} width={40} />
            </Fab>
          </Zoom>
        )}
        {!open && <MoveUpFAB />}

        <Navigation
          {...(useFab
            ? {
                variant: 'temporary',
                open,
                onClose: ({ force = true }) => {
                  if (force || !fabNavigation) {
                    setState({ open: false });
                  }
                },
              }
            : { variant: 'permanent' })}
          location={location}
        />
      </ErrorBoundary>

      {(!disableContainer && (
        <>
          <Container {...ContainerProps}>
            <ErrorBoundary location="content">{children}</ErrorBoundary>
          </Container>
          <Container disableMargin paper={false}>
            <Footer />
          </Container>
        </>
      )) ||
        children}
    </>
  );
}

export default Layout;
